import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, Filter, History, Loader2, RefreshCw } from "lucide-react";
import api from "../../services/api";


const CONTEXT_LABELS = {
  anamnese: "Anamnese",
  evolution: "Evolução clínica",
  odontogram: "Odontograma estruturado",
  document: "Documento clínico",
  finance: "Financeiro",
  patient_record: "Resumo do prontuário",
};

const STATUS_LABELS = {
  generated: "Gerada",
  accepted: "Confirmada",
  edited: "Editada",
  dismissed: "Descartada",
  stale: "Desatualizada",
};

const STATUS_STYLES = {
  generated: "bg-purple-100 text-purple-800",
  accepted: "bg-green-100 text-green-800",
  edited: "bg-blue-100 text-blue-800",
  dismissed: "bg-gray-100 text-gray-700",
  stale: "bg-amber-100 text-amber-900",
};

const getHighlights = (payload) => {
  const candidates = [
    ...(Array.isArray(payload?.risk_flags) ? payload.risk_flags : []),
    ...(Array.isArray(payload?.confirmation_items) ? payload.confirmation_items : []),
    ...(Array.isArray(payload?.blockers) ? payload.blockers : []),
    ...(Array.isArray(payload?.attention_items) ? payload.attention_items : []),
    ...(Array.isArray(payload?.consistency_items) ? payload.consistency_items : []),
  ];
  if (Array.isArray(payload?.missing_sections) && payload.missing_sections.length) {
    candidates.push(`Sem registros: ${payload.missing_sections.join(", ")}.`);
  }
  return candidates.filter(Boolean).slice(0, 4);
};


export default function ContextualAIHistory({ patientId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [contextType, setContextType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pageSize = 10;

  const loadHistory = useCallback(async (requestedPage = 1) => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/contextual-ai/analyses", {
        params: {
          patient_id: patientId,
          ...(contextType ? { context_type: contextType } : {}),
          page: requestedPage,
          page_size: pageSize,
        },
      });
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotal(Number(response.data?.total) || 0);
      setPage(Number(response.data?.page) || requestedPage);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível carregar o histórico da IA.");
    } finally {
      setLoading(false);
    }
  }, [patientId, contextType]);

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-purple-700" />
            <h3 className="text-lg font-semibold text-gray-900">Histórico da IA</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">Revisões geradas nos locais em que os registros foram preenchidos. Nenhuma nova análise é criada nesta tela.</p>
        </div>
        <button type="button" onClick={() => loadHistory(page)} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm text-gray-700 disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-lg border bg-gray-50 p-3">
        <Filter className="h-4 w-4 text-gray-500" />
        <label htmlFor="ai-history-context" className="text-sm text-gray-700">Filtrar por origem</label>
        <select id="ai-history-context" value={contextType} onChange={(event) => setContextType(event.target.value)} className="ml-auto rounded-lg border bg-white px-3 py-2 text-sm">
          <option value="">Todas</option>
          {Object.entries(CONTEXT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-500"><Loader2 className="h-5 w-5 animate-spin" /> Carregando histórico...</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-gray-500">Nenhuma revisão contextual foi gerada para este paciente.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const payload = item.payload || {};
            const highlights = getHighlights(payload);
            return (
              <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">{CONTEXT_LABELS[item.context_type] || item.context_type || "Revisão contextual"}</p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-gray-500"><Clock className="h-3.5 w-3.5" /> {new Date(item.created_at || item.generated_at).toLocaleString("pt-BR")}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_STYLES[item.status] || STATUS_STYLES.generated}`}>{STATUS_LABELS[item.status] || "Gerada"}</span>
                </div>
                {payload.summary && <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{payload.summary}</p>}
                {highlights.length > 0 && <ul className="mt-3 space-y-1 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{highlights.map((text, index) => <li key={index}>• {text}</li>)}</ul>}
                {item.status === "edited" && item.edited_content && <div className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900"><p className="font-medium">Conteúdo revisado pela profissional</p><p className="mt-1 whitespace-pre-wrap">{item.edited_content}</p></div>}
              </div>
            );
          })}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between border-t pt-4 text-sm">
          <span className="text-gray-600">Página {page} de {totalPages} · {total} registro(s)</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => loadHistory(page - 1)} disabled={loading || page <= 1} className="rounded-lg border px-3 py-1.5 disabled:opacity-50">Anterior</button>
            <button type="button" onClick={() => loadHistory(page + 1)} disabled={loading || page >= totalPages} className="rounded-lg border px-3 py-1.5 disabled:opacity-50">Próxima</button>
          </div>
        </div>
      )}
    </div>
  );
}
